import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../shared/api/client";
import { AppBadge, AppButton, AppField, AppInput } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCamera,
  IconCheck,
  IconError,
  IconOffline,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { getInstallation, lookupDeviceBySerial, postEvidence } from "../api";
import { customerDisplayName, useCustomersMap } from "../enrichment";
import type { DeviceLookupResponse } from "../types";

/**
 * S2 — Device serial scan & customer confirmation (handoff TASK-008, s2-*).
 * Manual serial entry (camera scan is a placeholder affordance — no scanner
 * dependency in this build) → device lookup → confirm the device belongs to
 * this customer → POST evidence { serial_number }. The server validates the
 * serial against the assigned device (422 on mismatch).
 * Offline: lookup and confirmation both need connectivity — notice state.
 */

type LookupState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "found"; device: DeviceLookupResponse["device"] }
  | { phase: "not-found" }
  | { phase: "error"; message: string };

export function S2ScanSerial() {
  const { id = "" } = useParams();
  const installationId = Number(id);
  const navigate = useNavigate();

  const [serial, setSerial] = useState("");
  const [lookup, setLookup] = useState<LookupState>({ phase: "idle" });

  const query = useQuery({
    queryKey: ["installation", installationId],
    queryFn: () => getInstallation(installationId),
    retry: 1,
    staleTime: 15_000,
  });

  const inst = query.data?.installation;
  const customers = useCustomersMap(useMemo(() => [inst?.customer_id ?? 0], [inst?.customer_id]));
  const customer = inst ? customers.get(inst.customer_id) : undefined;

  useFieldHeader(
    "Scan device serial",
    inst ? `${customerDisplayName(customer, inst.customer_id)} — INST-${inst.id}` : undefined,
  );
  useFieldJourney({ flow: "install", step: 1, backTo: "/field/installations" });

  const evidence = useMutation({
    mutationFn: (serialNumber: string) => postEvidence(installationId, { serial_number: serialNumber }),
    onSuccess: () => navigate(`/field/installations/${installationId}/evidence`),
  });

  const doLookup = async () => {
    const value = serial.trim();
    if (!value) return;
    setLookup({ phase: "loading" });
    try {
      const res = await lookupDeviceBySerial(value);
      setLookup({ phase: "found", device: res.device });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setLookup({ phase: "not-found" });
      } else {
        setLookup({ phase: "error", message: err instanceof Error ? err.message : "Lookup failed" });
      }
    }
  };

  // ── Loading / error ────────────────────────────────────────────────────────
  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Loading task…</h2>
      </div>
    );
  }

  if (query.isError || !inst) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Could not load task</h2>
        <p>{query.error instanceof Error ? query.error.message : "Something went wrong"}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  // ── Offline: serial check needs the device registry ────────────────────────
  if (!navigator.onLine) {
    return (
      <div className="pg-state">
        <span className="state-icon warning">
          <IconOffline size={40} />
        </span>
        <h2>Serial check needs connectivity</h2>
        <p>
          Device lookup and serial confirmation call the device registry. Reconnect and return to
          this step — earlier progress is saved.
        </p>
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => navigate("/field/installations")}
        >
          Back to tasks
        </AppButton>
      </div>
    );
  }

  // ── Already confirmed → continue ───────────────────────────────────────────
  if (inst.serial_confirmed) {
    return (
      <>
        <div className="pg-state">
          <span className="state-icon success">
            <IconCheck size={40} />
          </span>
          <h2>Serial confirmed</h2>
          <p>
            {inst.serial_confirmed_at
              ? `Confirmed at ${formatHHMMLocal(inst.serial_confirmed_at)}.`
              : "The device serial for this task is confirmed."}
          </p>
        </div>
        <div className="pg-actions">
          <AppButton
            type="button"
            onClick={() => navigate(`/field/installations/${installationId}/evidence`)}
          >
            Continue to evidence
          </AppButton>
        </div>
      </>
    );
  }

  // ── Evidence POST errors (server-side serial gate) ─────────────────────────
  const evErr = evidence.error instanceof ApiError ? evidence.error : null;
  const serialMismatch = evErr?.status === 422;

  const found = lookup.phase === "found" ? lookup.device : null;
  const assignedToOther =
    found?.customer != null && found.customer.id !== inst.customer_id;

  return (
    <>
      <AppField label="Device serial number">
        <div className="pg-scan-row">
          <AppInput
            value={serial}
            onChange={(e) => {
              setSerial(e.target.value);
              setLookup({ phase: "idle" });
            }}
            placeholder="e.g. PG-2026-000481"
            autoCapitalize="characters"
            aria-label="Device serial number"
          />
          {/* Placeholder affordance per design — no camera-scanner dependency
              in this build (documented deviation). */}
          <AppButton
            variant="secondary"
            type="button"
            className="pg-scan-btn"
            title="Camera scan — not available in this build; enter the serial manually"
            aria-label="Scan serial via camera (not available)"
            disabled
          >
            <IconCamera size={18} />
          </AppButton>
        </div>
      </AppField>

      {lookup.phase === "idle" || lookup.phase === "loading" ? (
        <div className="pg-actions">
          <AppButton
            type="button"
            onClick={() => void doLookup()}
            disabled={!serial.trim() || lookup.phase === "loading"}
          >
            {lookup.phase === "loading" ? <IconSpinner size={14} /> : null} Look up device
          </AppButton>
        </div>
      ) : null}

      {lookup.phase === "not-found" ? (
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} />
          Serial not recognised — check the digits on the device label and try again.
        </div>
      ) : null}

      {lookup.phase === "error" ? (
        <div className="pg-inline-error" role="alert">
          <IconError size={14} />
          Device lookup failed — {lookup.message}
        </div>
      ) : null}

      {found ? (
        <>
          <div className="pg-summary">
            <div className="row">
              <span className="k">Device</span>
              <span className="v">
                {[found.device_type_name, found.model].filter(Boolean).join(" · ") ||
                  found.serial_number}
              </span>
            </div>
            <div className="row">
              <span className="k">Manufacturer</span>
              <span className="v">{found.manufacturer_name ?? "—"}</span>
            </div>
            <div className="row">
              <span className="k">Status</span>
              <span className="v">
                {found.customer === null ? (
                  <AppBadge tone="info">Unassigned — free for this task</AppBadge>
                ) : (
                  <AppBadge tone={assignedToOther ? "error" : "success"}>
                    {assignedToOther ? "Assigned to another customer" : "Assigned to this customer"}
                  </AppBadge>
                )}
              </span>
            </div>
          </div>

          {assignedToOther ? (
            <div className="pg-inline-error" role="alert">
              <IconWarning size={14} />
              This device is registered to {found.customer?.name ?? "another customer"} — not to
              this task's customer. Check the serial or contact the office.
            </div>
          ) : (
            <>
              <div className="pg-summary">
                <div className="row">
                  <span className="k">Customer</span>
                  <span className="v">{customerDisplayName(customer, inst.customer_id)}</span>
                </div>
                <div className="row">
                  <span className="k">Ward / district</span>
                  <span className="v">
                    {[customer?.ward, customer?.district].filter(Boolean).join(" · ") || "—"}
                  </span>
                </div>
                <div className="row">
                  <span className="k">Contract</span>
                  <span className="v">
                    {customer?.active_contract?.contract_number ??
                      (inst.contract_id ? `#${inst.contract_id}` : "—")}
                  </span>
                </div>
              </div>

              {serialMismatch ? (
                <div className="pg-inline-error" role="alert">
                  <IconWarning size={14} />
                  {evErr?.message ??
                    "Serial does not match the device assigned to this installation."}
                </div>
              ) : null}
              {evidence.isError && !serialMismatch ? (
                <div className="pg-inline-error" role="alert">
                  <IconError size={14} />
                  {evidence.error instanceof Error
                    ? evidence.error.message
                    : "Confirmation failed — retry."}
                </div>
              ) : null}

              <div className="pg-actions">
                <AppButton
                  type="button"
                  onClick={() => evidence.mutate(found.serial_number)}
                  disabled={evidence.isPending}
                >
                  {evidence.isPending ? <IconSpinner size={14} /> : null} Confirm customer
                </AppButton>
              </div>
            </>
          )}
        </>
      ) : null}
    </>
  );
}

function formatHHMMLocal(iso: string): string {
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
